import { lazy, ReactNode, useEffect } from 'react';
import { createBrowserRouter, Navigate, RouteObject, RouterProvider, useLocation } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { Shell } from '../components/layout/Shell';
import { Guard, NotFound } from '../components/layout/Guard';
import { Loading } from '../components/ui';

const Login = lazy(() => import('../features/auth/LoginPage'));
const Forgot = lazy(() => import('../features/auth/ForgotPasswordPage'));
const Reset = lazy(() => import('../features/auth/ResetPasswordPage'));
const ForcedChange = lazy(() => import('../features/auth/ForcedChangePage'));

/**
 * Feature routes are discovered: each folder under src/features may export `routes.tsx` with a default
 * RouteObject[]. Adding a feature never means editing this file.
 */
const featureModules = import.meta.glob('../features/*/routes.tsx', { eager: true }) as Record<string, { default: RouteObject[] }>;
const featureRoutes: RouteObject[] = Object.values(featureModules).flatMap((m) => m.default);

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const must = useAuth((s) => s.user?.mustChangePassword);
  const loc = useLocation();
  if (status === 'loading') return <div className="auth-wrap"><Loading /></div>;
  if (status === 'anon') return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (must) return <Navigate to="/change-password" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  { path: '/login', element: <Login /> },
  { path: '/forgot-password', element: <Forgot /> },
  { path: '/reset-password', element: <Reset /> },
  { path: '/change-password', element: <ForcedChange /> },
  {
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      ...featureRoutes,
      { path: '*', element: <Guard any={[]}><NotFound /></Guard> },
    ],
  },
]);

export function App() {
  const bootstrap = useAuth((s) => s.bootstrap);
  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);
  return <RouterProvider router={router} />;
}
